import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { INBOX_FILTERS, CHANNELS, CHANNEL_META, scoreColor, fmtRM, STAGE_LABELS, STAGE_COLORS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Search, Bot, UserCheck, Send, Sparkles, Flame, CheckCheck, RotateCcw, FileText, Phone, Loader2, X,
  MessageSquare, Facebook, Instagram, MessageSquareText, Megaphone
} from "lucide-react";

const CHANNEL_ICON = { whatsapp: MessageSquare, messenger: Facebook, instagram: Instagram, comment: MessageSquareText };

const statusBadge = {
  AI_HANDLING: { label: "AI", cls: "bg-sky-100 text-sky-700", icon: Bot },
  STAFF_HANDLING: { label: "Staff", cls: "bg-emerald-100 text-emerald-700", icon: UserCheck },
  WAITING_STAFF: { label: "Waiting", cls: "bg-amber-100 text-amber-700", icon: null },
  CLOSED: { label: "Closed", cls: "bg-slate-100 text-slate-600", icon: null },
};

export default function Inbox() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("ALL");
  const [channel, setChannel] = useState("ALL");
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const scrollRef = useRef(null);

  const { data: convos = [] } = useQuery({
    queryKey: ["convos", filter, channel], queryFn: () => api.get(`/conversations?filter=${filter}&channel=${channel}`).then((r) => r.data),
  });
  const { data: active } = useQuery({
    queryKey: ["convo", activeId], queryFn: () => api.get(`/conversations/${activeId}`).then((r) => r.data),
    enabled: !!activeId,
  });

  useEffect(() => {
    if (!activeId && convos.length) setActiveId(convos[0].id);
  }, [convos, activeId]);

  useEffect(() => {
    setSummary(null);
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active]);

  const filtered = convos.filter((c) => c.contact_name.toLowerCase().includes(search.toLowerCase()));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["convo", activeId] });
    qc.invalidateQueries({ queryKey: ["convos"] });
  };

  const sendMsg = async () => {
    if (!text.trim() || !activeId) return;
    const t = text; setText("");
    await api.post(`/conversations/${activeId}/messages`, { text: t });
    refresh();
  };

  const aiReply = async () => {
    setBusy(true);
    try {
      await api.post(`/conversations/${activeId}/ai-reply`);
      toast.success("AI Reply dihantar");
      refresh();
    } catch { toast.error("AI tidak tersedia"); } finally { setBusy(false); }
  };

  const takeover = async () => {
    await api.post(`/conversations/${activeId}/takeover`);
    toast.success("Human Takeover diaktifkan");
    refresh();
  };
  const returnAi = async () => {
    await api.post(`/conversations/${activeId}/return-ai`);
    toast.success("Dikembalikan kepada AI");
    refresh();
  };

  const suggestReply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ai/reply-suggest/${activeId}`);
      setText(r.data.suggestion);
      toast.success("Cadangan balasan dijana");
    } catch { toast.error("AI tidak tersedia"); } finally { setBusy(false); }
  };

  const genSummary = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ai/summary/${activeId}`);
      setSummary(r.data.summary);
    } catch { toast.error("AI tidak tersedia"); } finally { setBusy(false); }
  };

  const contact = active?.contact;

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="inbox-page">
      {/* Left: list */}
      <div className="w-full sm:w-80 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="inbox-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari perbualan..." className="w-full h-9 pl-9 pr-3 rounded-full bg-secondary text-sm outline-none" />
          </div>
          <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="inbox-channel-filters">
            {CHANNELS.map((ch) => (
              <button key={ch.key} onClick={() => setChannel(ch.key)}
                data-testid={`inbox-channel-${ch.key.toLowerCase()}`}
                className={`shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors ${
                  channel === ch.key ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {ch.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1 scrollbar-hide">
            {INBOX_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                data-testid={`inbox-tab-${f.key.toLowerCase().replace(/_/g, "-")}`}
                className={`shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors ${
                  filter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Tiada perbualan.</p>}
          {filtered.map((c) => {
            const sb = statusBadge[c.status] || statusBadge.AI_HANDLING;
            const chKey = (c.channel || "whatsapp");
            const cm = CHANNEL_META[chKey] || CHANNEL_META.whatsapp;
            const ChIcon = CHANNEL_ICON[chKey] || MessageSquare;
            return (
              <button key={c.id} onClick={() => setActiveId(c.id)}
                data-testid={`inbox-conversation-${c.id}`}
                className={`w-full text-left p-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${activeId === c.id ? "bg-accent/50" : ""}`}>
                <div className="flex items-center gap-2">
                  <div className="relative shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                      {c.contact_name[0]}
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ${cm.cls} flex items-center justify-center border-2 border-card`}>
                      <ChIcon className="h-2.5 w-2.5" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm truncate">{c.contact_name}</p>
                      {c.lead_score >= 85 && <Flame className="h-3.5 w-3.5 text-rose-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 pl-12">
                  <Badge className={`${sb.cls} border-0 text-[10px] px-1.5 py-0 rounded`}>{sb.label}</Badge>
                  {c.ad && <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px] px-1.5 py-0 rounded"><Megaphone className="h-2.5 w-2.5 mr-0.5" />Ad</Badge>}
                  {c.unread > 0 && <span className="h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">{c.unread}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Center: thread */}
      <div className="flex-1 hidden sm:flex flex-col min-w-0">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Pilih perbualan</div>
        ) : (
          <>
            <div className="h-14 border-b border-border flex items-center justify-between px-4 bg-card">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">{active.contact_name[0]}</div>
                <div>
                  <p className="font-semibold text-sm">{active.contact_name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{active.contact_phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {active.status === "STAFF_HANDLING" ? (
                  <Button size="sm" variant="outline" onClick={returnAi} data-testid="button-return-ai" className="rounded-full">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Return to AI
                  </Button>
                ) : (
                  <Button size="sm" onClick={takeover} data-testid="button-human-takeover" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    <UserCheck className="h-4 w-4 mr-1.5" /> Human Takeover
                  </Button>
                )}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto chat-wallpaper p-4 space-y-2" data-testid="inbox-chat-thread">
              {active.messages.map((m) => {
                const isIn = m.sender === "in";
                return (
                  <div key={m.id} className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                      isIn ? "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-tl-sm" : "bg-[#DCF8C6] dark:bg-emerald-900 text-slate-800 dark:text-slate-100 rounded-tr-sm"}`}>
                      {m.sender === "ai" && <span className="flex items-center gap-1 text-[10px] font-semibold text-primary mb-0.5"><Bot className="h-3 w-3" /> AI</span>}
                      {m.sender === "out" && <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 mb-0.5"><UserCheck className="h-3 w-3" /> Staff</span>}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      <span className="flex items-center justify-end gap-1 text-[10px] text-slate-400 mt-0.5">
                        {new Date(m.timestamp).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" })}
                        {!isIn && <CheckCheck className="h-3 w-3 text-sky-500" />}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-border bg-card p-3">
              <div className="flex items-center gap-2 mb-2">
                <Button size="sm" variant="outline" onClick={aiReply} disabled={busy} data-testid="button-ai-reply" className="rounded-full text-xs">
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Bot className="h-3.5 w-3.5 mr-1" />} AI Reply
                </Button>
                <Button size="sm" variant="outline" onClick={suggestReply} disabled={busy} data-testid="button-ai-suggest-reply" className="rounded-full text-xs">
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> Suggest Reply
                </Button>
              </div>
              <div className="flex items-end gap-2">
                <textarea data-testid="chat-input-textarea" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                  rows={1} placeholder="Taip mesej..."
                  className="flex-1 resize-none rounded-2xl bg-secondary px-4 py-2.5 text-sm outline-none max-h-32" />
                <Button size="icon" onClick={sendMsg} data-testid="chat-send-button" className="rounded-full h-10 w-10 shrink-0">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right: customer panel */}
      {contact && (
        <div className="w-80 shrink-0 border-l border-border bg-card hidden lg:flex flex-col overflow-y-auto">
          <div className="p-5 text-center border-b border-border">
            <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xl mx-auto">{contact.name[0]}</div>
            <p className="font-display font-semibold mt-3">{contact.name}</p>
            <p className="text-xs text-muted-foreground">{contact.phone}</p>
            <div className="mt-3 flex justify-center">
              <Badge data-testid="customer-panel-lead-score" className={`${scoreColor(contact.lead_score)} border-0 rounded-full`}>
                <Flame className="h-3.5 w-3.5 mr-1" /> {contact.tier} · {contact.lead_score}/100
              </Badge>
            </div>
          </div>

          <div className="p-4 space-y-3 text-sm border-b border-border">
            {active?.channel && (
              <Row label="Channel" value={<Badge className={`${(CHANNEL_META[active.channel] || CHANNEL_META.whatsapp).cls} border-0 text-[10px] rounded capitalize`}>{active.channel}</Badge>} />
            )}
            <Row label="Interest" value={contact.interest} />
            <Row label="Source" value={contact.source} />
            {active?.campaign && <Row label="Campaign" value={active.campaign} />}
            {active?.ad && (
              <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 p-2.5 flex items-start gap-2" data-testid="inbox-ad-attribution">
                <Megaphone className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">Ad Attribution</p>
                  <p className="text-xs text-foreground">{active.ad}</p>
                </div>
              </div>
            )}
            <Row label="Stage" value={<Badge className={`${STAGE_COLORS[contact.stage]} border-0 text-[10px] rounded`}>{STAGE_LABELS[contact.stage]}</Badge>} />
            <Row label="Revenue" value={fmtRM(contact.revenue)} />
            <Row label="Opt-in" value={contact.opt_in && !contact.opt_out ? <span className="text-emerald-600 font-medium">YES</span> : <span className="text-rose-600">NO</span>} />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {contact.tags?.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">#{t}</span>)}
            </div>
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">AI Summary</span>
              <Button size="sm" variant="ghost" onClick={genSummary} disabled={busy} data-testid="button-ai-summary" className="h-7 text-xs">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              </Button>
            </div>
            {summary ? (
              <div className="rounded-xl bg-accent/50 p-3 text-xs whitespace-pre-wrap text-accent-foreground relative">
                <button onClick={() => setSummary(null)} className="absolute top-2 right-2"><X className="h-3.5 w-3.5" /></button>
                {summary}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Klik ikon untuk jana ringkasan AI perbualan ini.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value}</span>
  </div>
);
